import React, { useEffect, useState } from "react"
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner,
  FormGroup, Label, Input, Row, Col, Alert, FormFeedback,
} from "reactstrap"
import Select from "react-select"
import { toast } from "react-toastify"
import { adminAssignAddon } from "../../../api/requests/addon-requests"
import { fetchCompaniesNames } from "../../../api/requests/account-requests"

const NAVY = "#221C5D"

/**
 * Süper yöneticinin bir firmaya ücretsiz modül tanımlaması.
 *
 * <p>Tarih alanları boş bırakılabilir; backend o zaman modül tipine göre karar
 * verir (aboneliğe bağlı modülde aboneliğin bitişi, tükenen modülde varsa
 * geçerlilik süresi). Bilinçli olarak burada tahmin yürütülmez — aynı kuralı
 * iki yerde hesaplamak, zamanla ayrışan iki cevap üretirdi.
 *
 * <p>Aktif aboneliği olmayan bir firmaya aboneliğe bağlı modül tanımlanırken
 * backend bitiş tarihini belirleyemez ve elle girilmesini ister; hata mesajı
 * doğrudan gösterilir.
 */
const AddonAssignModal = ({ isOpen, toggle, module, onAssigned }) => {
  const [companies, setCompanies] = useState([])
  const [companyId, setCompanyId] = useState(null)
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [note, setNote] = useState("")
  const [saving, setSaving] = useState(false)
  // Backend'in reddettiği son deneme. Toast kaybolur; kullanıcı formda hangi
  // alanı düzelteceğini görebilmeli.
  const [formError, setFormError] = useState(null)
  const [endDateRequired, setEndDateRequired] = useState(false)
  const [loadingCompanies, setLoadingCompanies] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setCompanyId(null)
    setStartDate("")
    setEndDate("")
    setNote("")
    setFormError(null)
    setEndDateRequired(false)
    let cancelled = false
    ;(async () => {
      try {
        setLoadingCompanies(true)
        const res = await fetchCompaniesNames()
        if (!cancelled) setCompanies(res?.data || [])
      } catch (err) {
        if (!cancelled) toast.error(err.message || "Firmalar yüklenemedi")
      } finally {
        if (!cancelled) setLoadingCompanies(false)
      }
    })()
    return () => { cancelled = true }
  }, [isOpen])

  const companyOptions = companies.map((c) => ({
    value: c.id,
    label: c.companyName || c.companyCode || "(isimsiz firma)",
  }))

  const handleSubmit = async () => {
    if (!companyId) {
      setFormError("Firma seçmelisiniz")
      toast.error("Firma seçmelisiniz")
      return
    }
    setFormError(null)
    try {
      setSaving(true)
      const res = await adminAssignAddon({
        companyId,
        moduleId: module?.id,
        startDate: startDate || null,
        endDate: endDate || null,
        note: note || null,
      })
      toast.success(res?.message || "Modül firmaya tanımlandı")
      if (onAssigned) onAssigned(res?.data)
      toggle()
    } catch (err) {
      const message = err.message || "Modül tanımlanamadı"
      setFormError(message)
      // Aboneliği olmayan firmaya aboneliğe bağlı modül tanımlanırken backend
      // bitiş tarihini hesaplayamaz ve elle girilmesini ister. Hangi alanın
      // beklendiğini metinden anlamak kullanıcının işi olmamalı.
      const needsEndDate = message.includes("bitiş tarihi")
      setEndDateRequired(needsEndDate)
      toast.error(message)
      if (needsEndDate) {
        document.querySelector('[data-addon-end-date] input')?.focus()
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>
        <i className="bx bx-user-plus me-2" style={{ color: NAVY }} />
        Firmaya Tanımla
      </ModalHeader>
      <ModalBody>
        <div className="bg-light rounded-3 p-3 mb-3">
          <div className="d-flex justify-content-between">
            <span className="text-muted" style={{ fontSize: ".82rem" }}>Modül</span>
            <span className="fw-semibold">{module?.name}</span>
          </div>
          <div className="d-flex justify-content-between mt-1">
            <span className="text-muted" style={{ fontSize: ".82rem" }}>Tip</span>
            <span>{module?.lifecycleLabel || "-"}</span>
          </div>
        </div>

        {formError && (
          <Alert color="danger" className="py-2" style={{ fontSize: ".85rem" }}>
            {formError}
          </Alert>
        )}

        <FormGroup>
          <Label>Firma</Label>
          <Select
            options={companyOptions}
            isLoading={loadingCompanies}
            placeholder="Firma seçin..."
            noOptionsMessage={() => "Firma bulunamadı"}
            value={companyOptions.find((o) => o.value === companyId) || null}
            onChange={(opt) => setCompanyId(opt ? opt.value : null)}
          />
        </FormGroup>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>Başlangıç</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              <div className="text-muted" style={{ fontSize: ".72rem" }}>Boş = bugün</div>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup data-addon-end-date>
              <Label>
                Bitiş
                {endDateRequired && <span className="text-danger ms-1">*</span>}
              </Label>
              <Input
                type="date"
                value={endDate}
                invalid={endDateRequired && !endDate}
                onChange={(e) => {
                  setEndDate(e.target.value)
                  if (e.target.value) setEndDateRequired(false)
                }}
              />
              <FormFeedback>Bu firma için bitiş tarihi zorunlu</FormFeedback>
              <div className="text-muted" style={{ fontSize: ".72rem" }}>
                {endDateRequired ? "Firmanın aktif aboneliği yok" : "Boş = modül tipine göre"}
              </div>
            </FormGroup>
          </Col>
        </Row>

        <FormGroup className="mb-0">
          <Label>Not</Label>
          <Input
            type="textarea"
            rows={2}
            maxLength={500}
            placeholder="Neden tanımlandığı (destek kaydı, kampanya vb.)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </FormGroup>

        <div className="mt-3 p-3 rounded-3" style={{ background: "#EBF0F7" }}>
          <i className="bx bx-gift me-1" style={{ color: NAVY }} />
          <span style={{ fontSize: ".8rem" }}>
            Bu tanımlama <strong>ücretsizdir</strong>; ödeme alınmaz ve ciro
            raporlarında satış olarak görünmez.
          </span>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={saving}>İptal</Button>
        <Button
          style={{ background: NAVY, borderColor: NAVY, color: "#fff" }}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? <Spinner size="sm" /> : "Tanımla"}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default AddonAssignModal
